<template>
  <v-container fluid>
    <v-container fluid style="max-width: 1200px;">
      <!-- 헤더 카드 -->
      <v-card 
        class="mx-auto"
        style="margin-bottom: 10px;"
        rounded="lg"
        outlined
      >
        <v-container fluid style="margin: 10px;">
          <h3 style="margin-bottom: 5px;">MEASLES Analysis</h3>
          <span style="font-size:0.8rem;">Upload zip file for MEASLES analysis and download results.</span>
        </v-container>
      </v-card>

      <!-- 파일 업로드 카드 -->
      <v-card 
        class="mx-auto"
        style="margin-bottom: 10px;"
        rounded="lg"
        outlined
      >
        <v-container fluid style="margin: 10px;">
          <h4 style="margin-bottom: 15px;">
            <v-icon size="20" style="margin-right: 10px;">mdi-cloud-upload</v-icon>
            File Upload
          </h4>
          <v-row align="center" style="align-items: center;">
            <v-col cols="12" md="3" style="display: flex; align-items: center;">
              <v-text-field
                v-model="processName"
                label="Process Name"
                placeholder="e.g., process_001"
                outlined
                dense
                hide-details
                :disabled="uploading"
                :rules="processNameRules"
                style="width: 100%;"
                @input="validateProcessName"
              />
            </v-col>
            <v-col cols="12" md="5" style="display: flex; align-items: center; padding-left: 12px;">
              <v-file-input
                v-model="selectedFiles"
                accept=".zip"
                label="Select ZIP files"
                prepend-icon="mdi-folder-zip"
                outlined
                dense
                hide-details
                show-size
                multiple
                chips
                :disabled="uploading"
                style="flex: 1;"
                @change="onFileChange"
              >
                <template v-slot:selection="{ index, text }">
                  <v-chip
                    v-if="index < 2"
                    small
                    label
                    color="primary"
                  >
                    {{ text }}
                  </v-chip>
                  <span
                    v-else-if="index === 2"
                    class="text-overline grey--text text--darken-3 mx-2"
                  >
                    +{{ selectedFiles.length - 2 }} File(s)
                  </span>
                </template>
              </v-file-input>
            </v-col>
            <v-col cols="12" md="4" style="display: flex; align-items: center; padding-left: 16px;">
              <v-btn
                color="#78909C"
                :loading="uploading"
                :disabled="!selectedFiles || selectedFiles.length === 0 || !processName || uploading"
                outlined
                @click="uploadFiles"
              >
                <v-icon left>mdi-upload</v-icon>
                Upload ({{ selectedFiles ? selectedFiles.length : 0 }})
              </v-btn>
            </v-col>
          </v-row>
          <div v-if="uploading" style="margin-top: 15px;">
            <v-progress-linear
              :value="uploadProgress"
              color="#78909C"
              height="28"
              striped
              style="border-radius: 4px;"
            />
            <div style="margin-top: 8px; font-size: 0.875rem; font-weight: 500; color: #424242; text-align: center;">
              {{ uploadingFileName || 'Uploading...' }} - {{ Math.ceil(uploadProgress) }}%
            </div>
            <div style="margin-top: 4px; font-size: 0.8rem; color: #666; text-align: center;">
              Uploading {{ currentFileIndex }} of {{ totalFiles }} files...
            </div>
          </div>
        </v-container>
      </v-card>

      <!-- 테이블 카드 -->
      <v-card 
        class="mx-auto"
        rounded="lg"
        outlined
      >
        <v-container fluid style="margin: 10px; padding: 10px 20px 10px 10px;">
          <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 15px;">
            <h4>
              <v-icon size="20" style="margin-right: 10px;">mdi-table</v-icon>
              Upload History
            </h4>
            <v-btn
              color="#78909C"
              outlined
              small
              :loading="loading"
              @click="fetchData"
            >
              <v-icon small>mdi-refresh</v-icon>
              <span style="margin-left: 5px;">Refresh</span>
            </v-btn>
          </div>
          
          <div style="overflow-x: auto; padding-right: 10px;">
            <v-data-table
              :headers="headers"
              :items="tableData"
              :loading="loading"
              loading-text="Loading data..."
              no-data-text="No uploaded files."
              class="elevation-1"
              style="margin-right: 10px;"
              :items-per-page="10"
              :footer-props="{
                'items-per-page-options': [5, 10, 20, 50]
              }"
            >
            <!-- 로우 번호 -->
            <template v-slot:item.rowNum="{ index }">
              {{ index + 1 }}
            </template>
            
            <!-- 파일명 표시 (originalFileNames 배열이 있으면 표시) -->
            <template v-slot:item.fileName="{ item }">
              <div v-if="Array.isArray(item.originalFileNames) && item.originalFileNames.length > 0">
                <div v-for="(name, idx) in item.originalFileNames" :key="idx" style="font-size: 0.85rem;">
                  {{ name }}
                </div>
              </div>
              <div v-else>
                {{ item.fileName }}
              </div>
            </template>
            
            <!-- 상태 표시 -->
            <template v-slot:item.state="{ item }">
              <v-chip
                :color="getStateColor(item.state)"
                small
                text-color="white"
              >
                {{ item.state || 'UPLOADED' }}
              </v-chip>
            </template>
            
            <!-- 업로드 시간 포맷팅 -->
            <template v-slot:item.uploadedAt="{ item }">
              {{ formatDate(item.uploadedAt) }}
            </template>
            
            <!-- 완료 시간 포맷팅 -->
            <template v-slot:item.completedAt="{ item }">
              {{ item.completedAt ? formatDate(item.completedAt) : '-' }}
            </template>
            
            <!-- 다운로드 버튼 -->
            <template v-slot:item.download="{ item }">
              <v-btn
                v-if="item.resultAvailable"
                color="#78909C"
                small
                outlined
                :loading="item.downloading"
                @click="downloadResult(item)"
              >
                <v-icon small left>mdi-download</v-icon>
                Download
              </v-btn>
              <v-chip
                v-else
                small
                color="grey"
                text-color="white"
              >
                Processing
              </v-chip>
            </template>
          </v-data-table>
          </div>
        </v-container>
      </v-card>
    </v-container>

    <!-- Snackbar for notifications -->
    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="3000">
      {{ snackbarText }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
export default {
  name: 'Virus',
  data: () => ({
    selectedFiles: [],
    processName: '',
    processNameRules: [
      v => !!v || 'Process name is required',
      v => (v && v.trim().length > 0) || 'Process name cannot be empty',
      v => !v || /^[a-zA-Z0-9_]+$/.test(v) || 'Only English letters, numbers, and underscores are allowed'
    ],
    uploading: false,
    uploadProgress: 0,
    uploadingFileName: '',
    currentFileIndex: 0,
    totalFiles: 0,
    loading: false,
    snackbar: false,
    snackbarText: '',
    snackbarColor: 'success',
    headers: [
      { text: 'No.', value: 'rowNum', sortable: false, width: '60px' },
      { text: 'Process Name', value: 'processName', sortable: true },
      { text: 'File Name', value: 'fileName', sortable: true },
      { text: 'State', value: 'state', sortable: true, width: '120px' },
      { text: 'Upload Time', value: 'uploadedAt', sortable: true },
      { text: 'Completed Time', value: 'completedAt', sortable: true },
      { text: 'Result', value: 'download', sortable: false, align: 'center', width: '150px' }
    ],
    tableData: []
  }),
  created() {
    this.$axios.defaults.headers.common["x-access-token"] = this.$store.getters.getAccessToken;
    this.$axios.defaults.headers.common["x-refresh-token"] = this.$store.getters.getRefreshToken;
    this.fetchData();
  },
  methods: {
    validateProcessName() {
      // 영어, 숫자, 언더바만 허용하도록 필터링
      if (this.processName) {
        this.processName = this.processName.replace(/[^a-zA-Z0-9_]/g, '');
      }
    },
    onFileChange(files) {
      if (!files || files.length === 0) return;
      
      // 모든 파일이 .zip인지 확인
      const invalidFiles = files.filter(file => !file.name.endsWith('.zip'));
      if (invalidFiles.length > 0) {
        this.showSnackbar('Only ZIP files are allowed.', 'error');
        this.selectedFiles = files.filter(file => file.name.endsWith('.zip'));
      }
    },
    
    async uploadFiles() {
      if (!this.processName || !this.processName.trim()) {
        this.showSnackbar('Please enter a process name.', 'warning');
        return;
      }
      
      if (!this.selectedFiles || this.selectedFiles.length === 0) {
        this.showSnackbar('Please select files first.', 'warning');
        return;
      }
      
      this.uploading = true;
      this.totalFiles = this.selectedFiles.length;
      this.currentFileIndex = 0;
      
      // 백엔드가 upload.array("files", 50)로 여러 파일을 한 번에 받을 수 있으므로
      // 모든 파일을 하나의 FormData에 담아서 한 번에 전송
      const formData = new FormData();
      this.selectedFiles.forEach(file => {
        formData.append('files', file);
      });
      formData.append('processName', this.processName.trim());
      
      try {
        const response = await this.$axios.post(`${this.$apiHost}/virus/upload`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total) {
              this.uploadProgress = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
            }
            // 전체 파일 중 현재 진행 중인 파일 표시
            if (this.totalFiles > 0) {
              this.currentFileIndex = Math.min(
                Math.floor((progressEvent.loaded / progressEvent.total) * this.totalFiles) + 1,
                this.totalFiles
              );
            }
          }
        });
        
        // 백엔드 응답: { success: true, message: "...", data: { ... } }
        if (response.data && response.data.success) {
          const uploadedCount = response.data.data?.uploadedFiles?.length || this.totalFiles;
          this.showSnackbar(
            response.data.message || `All ${uploadedCount} file(s) uploaded successfully!`,
            'success'
          );
        } else {
          this.showSnackbar(`All ${this.totalFiles} file(s) uploaded successfully!`, 'success');
        }
        
        this.selectedFiles = [];
        this.processName = '';
        this.fetchData();
      } catch (error) {
        console.error('Upload error:', error);
        if (error.response && error.response.data) {
          // 백엔드 에러 응답: { success: false, message: "..." }
          const errorMessage = error.response.data.message || 'Failed to upload files. Please try again.';
          this.showSnackbar(errorMessage, 'error');
        } else {
          this.showSnackbar('Failed to upload files. Please try again.', 'error');
        }
      } finally {
        this.uploading = false;
        this.uploadProgress = 0;
        this.uploadingFileName = '';
        this.currentFileIndex = 0;
        this.totalFiles = 0;
      }
    },
    
    async fetchData() {
      this.loading = true;
      
      try {
        const response = await this.$axios.get(`${this.$apiHost}/virus/list`);
        
        // 백엔드 응답: 배열을 직접 반환
        // [{ id, fileName, originalFileNames, uploadedAt, state, resultAvailable, ... }]
        if (response.status >= 200 && response.status < 300) {
          if (Array.isArray(response.data)) {
            this.tableData = response.data.map(item => ({
              ...item,
              downloading: false,
              // originalFileNames가 배열인 경우 문자열로 변환 (표시용)
              fileNameDisplay: Array.isArray(item.originalFileNames) 
                ? item.originalFileNames.join(', ') 
                : item.fileName
            }));
          } else {
            // 배열이 아닌 경우 빈 배열로 초기화
            this.tableData = [];
          }
        } else {
          this.tableData = [];
        }
      } catch (error) {
        console.error('Fetch error:', error);
        
        // 모든 에러 케이스에서 빈 배열로 초기화 (에러 메시지 표시 안 함)
        this.tableData = [];
        
        // 실제 서버 다운이나 심각한 네트워크 문제만 표시
        if (!error.response && error.code === 'ECONNABORTED') {
          this.showSnackbar('Request timeout. Please try again.', 'error');
        } else if (error.response && error.response.status >= 500) {
          // 5xx 서버 에러만 표시
          const errorMessage = error.response.data?.message || 'Server error. Please try again later.';
          this.showSnackbar(errorMessage, 'error');
        }
      } finally {
        this.loading = false;
      }
    },
    
    async downloadResult(item) {
      item.downloading = true;
      
      try {
        const response = await this.$axios.get(
          `${this.$apiHost}/virus/download/${item.id}`,
          { 
            responseType: 'blob',
            // Content-Disposition 헤더에서 파일명 추출을 위해 헤더 확인
            headers: {
              'Accept': 'application/zip'
            }
          }
        );
        
        // Content-Disposition 헤더에서 파일명 추출, 없으면 processName 또는 fileName 사용
        let fileName = `${item.processName || item.fileName}_result.zip`;
        const contentDisposition = response.headers['content-disposition'];
        if (contentDisposition) {
          const fileNameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (fileNameMatch && fileNameMatch[1]) {
            fileName = decodeURIComponent(fileNameMatch[1].replace(/['"]/g, ''));
          }
        }
        
        // Blob 생성 및 다운로드
        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        this.showSnackbar('Download started!', 'success');
      } catch (error) {
        console.error('Download error:', error);
        
        // 백엔드 에러 응답 처리 (blob으로 받았을 수 있으므로 JSON 파싱 시도)
        if (error.response && error.response.data) {
          try {
            // blob을 텍스트로 변환하여 JSON 파싱 시도
            const text = await new Blob([error.response.data]).text();
            const errorData = JSON.parse(text);
            const errorMessage = errorData.message || 'Failed to download file.';
            this.showSnackbar(errorMessage, 'error');
          } catch (parseError) {
            // JSON 파싱 실패 시 기본 메시지
            if (error.response.status === 404) {
              this.showSnackbar('File not found.', 'error');
            } else if (error.response.status === 400) {
              this.showSnackbar('Analysis result is not ready yet.', 'error');
            } else {
              this.showSnackbar('Failed to download file.', 'error');
            }
          }
        } else {
          this.showSnackbar('Failed to download file.', 'error');
        }
      } finally {
        item.downloading = false;
      }
    },
    
    formatDate(dateString) {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleString('ko-KR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      });
    },
    
    showSnackbar(text, color = 'success') {
      this.snackbarText = text;
      this.snackbarColor = color;
      this.snackbar = true;
    },
    
    getStateColor(state) {
      const stateMap = {
        'UPLOADED': 'blue',
        'PROCESSING': 'orange',
        'COMPLETED': 'green',
        'FAILED': 'red',
        'ERROR': 'red'
      };
      return stateMap[state] || 'grey';
    }
  }
};
</script>

<style scoped>
.v-data-table {
  border-radius: 8px;
}
</style>

